import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ParentReportContent } from "../components/reports/ParentReportContent";
import { exportToPDF, printPage } from "../utils/pdfExport";
import {
  DocumentArrowDownIcon,
  PrinterIcon,
  ExclamationTriangleIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";

const API_BASE = process.env.REACT_APP_API_URL || "/api";

const PublicParentReport = () => {
  const { token } = useParams();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await fetch(`${API_BASE}/reports/parent/${token}`);
        const data = await response.json();

        if (!response.ok) {
          setError(data.error || "Failed to load report");
          return;
        }

        setReportData(data);
        if (data.categories) {
          setExpandedCategories(new Set(data.categories.map((c) => c.id)));
        }
      } catch (err) {
        setError("Unable to connect to the server. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchReport();
    }
  }, [token]);

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const filename = `${reportData.player.name.replace(/\s+/g, "-")}-Evaluation-Report`;
      await exportToPDF("parent-report-content", filename);
    } catch (err) {
      console.error("PDF export error:", err);
    }
    setExporting(false);
  };

  const handlePrint = () => {
    printPage();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const isExpired =
      error.includes("expired") || error.includes("no longer available");

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          {isExpired ? (
            <ClockIcon className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          ) : (
            <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          )}
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {isExpired ? "Report Link Expired" : "Unable to Load Report"}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            If you need access to this report, please contact your club
            administrator for a new link.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Minimal header */}
      <div className="bg-white border-b border-gray-200 print:hidden">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-lg font-bold text-slate-600">Eval Ninja</span>
            <span className="text-gray-400">|</span>
            <span className="text-sm text-gray-600">Evaluation Report</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <PrinterIcon className="w-4 h-4 mr-1.5" />
              Print
            </button>
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-slate-600 border border-transparent rounded-md hover:bg-slate-700 disabled:opacity-50"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-1.5" />
              {exporting ? "Exporting..." : "Download PDF"}
            </button>
          </div>
        </div>
      </div>

      {/* Report content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <ParentReportContent
          reportData={reportData}
          expandedCategories={expandedCategories}
          onToggleCategory={toggleCategory}
        />
      </div>

      {/* Footer */}
      <div className="bg-gray-100 border-t border-gray-200 py-4 text-center text-sm text-gray-500 print:hidden">
        <p>Powered by Eval Ninja</p>
      </div>
    </div>
  );
};

export default PublicParentReport;
